import type { FC } from "react";
import { useMemo } from "react";

import { Barcode } from "@ui/components/Barcode";
import LoyaltyLogo from "@ui/icons/loyalty_logo.svg";
import { useTranslations } from "@ui/providers/useTranslations";
import { formatDate } from "@ui/utils/formatDate/formatDate";
import classNames from "classnames";
import { QRCodeSVG } from "qrcode.react";

import { useConfig } from "@jti/app-context/useConfig";
import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";
import { formatFullName, ISO_DATE_FORMAT } from "@jti/fe-common";

import styles from "./ClubCard.module.scss";

type Variant = "club" | "tier1" | "tier2" | "tier3";

export type ClubCardProps = {
  variant?: Variant;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  userId: string;
  referralCode: string;
  logoSrc?: string;
};

type ClubCardNameProps = {
  firstName: string;
  lastName: string;
};

type ClubCardLogoWithCodeProps = {
  referralCode: string;
  logoSrc?: string;
};

const ClubCardName: FC<ClubCardNameProps> = ({ firstName, lastName }) => {
  const { __ } = useTranslations();
  const { isReversedNameOrder } = useConfig();

  return (
    <div className={styles.nameBlock}>
      <p className={styles.label}>{__("libs.ui.club_card.name_label")}</p>
      <p className={styles.text}>{formatFullName(firstName, lastName, !!isReversedNameOrder)}</p>
    </div>
  );
};

const ClubCardLogoWithCode: React.FC<ClubCardLogoWithCodeProps> = ({ referralCode, logoSrc }) => {
  const { __ } = useTranslations();

  return (
    <>
      {logoSrc ? (
        <ImageBehaviour src={logoSrc} alt="logo" className={styles.logo} data-testid="logo" />
      ) : (
        <LoyaltyLogo className={styles.logo} data-testid="logo" />
      )}

      {referralCode && (
        <div className={styles.referralCodeBlock}>
          <p className={styles.label}>{__("libs.ui.club_card.referral_code_label")}</p>
          <p className={styles.text}>{referralCode}</p>
        </div>
      )}
    </>
  );
};

export const ClubCard: React.FC<ClubCardProps> = ({
  variant = "club",
  firstName,
  lastName,
  dateOfBirth,
  email,
  userId,
  referralCode,
  logoSrc,
}) => {
  const { LOYALTY_CARD_TYPE, isReversedNameOrder } = useConfig();
  const { __ } = useTranslations();
  const isBarcodeClubCard = LOYALTY_CARD_TYPE === "bar_code";

  const userJsonForQr = useMemo(() => {
    if (isBarcodeClubCard) return null;

    return JSON.stringify({
      uuid: userId,
      code: referralCode,
      name: formatFullName(firstName, lastName, isReversedNameOrder),
      dob: formatDate(dateOfBirth, ISO_DATE_FORMAT),
      email: email,
    });
  }, [isBarcodeClubCard, userId, firstName, lastName, isReversedNameOrder, referralCode, email, dateOfBirth]);

  return (
    <div
      className={classNames(
        styles.clubCard,
        styles[`variant-${variant}`],
        isBarcodeClubCard ? styles.barcodeLayout : styles.qrcodeLayout
      )}
      data-testid="clubCard"
    >
      {isBarcodeClubCard ? (
        <>
          <div className={styles.clubCardColumn}>
            <div className={styles.clubCardRow}>
              <ClubCardLogoWithCode referralCode={referralCode} logoSrc={logoSrc} />
            </div>
            <ClubCardName firstName={firstName} lastName={lastName} />
          </div>
          <div className={styles.barcodeWrapper}>
            <Barcode value={userId} />
          </div>
        </>
      ) : (
        <>
          <div className={styles.clubCardRow}>
            <div className={styles.clubCardColumn}>
              <ClubCardLogoWithCode referralCode={referralCode} logoSrc={logoSrc} />
            </div>
            <div className={styles.qrCodeWrapper}>
              <QRCodeSVG value={userJsonForQr ?? ""} data-testid="qrCode" />
            </div>
          </div>
          <ClubCardName firstName={firstName} lastName={lastName} />
        </>
      )}
    </div>
  );
};
